import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { PageHeader } from "../components/Section";
import { CLIENTS, TESTIMONIALS } from "../data/site";

const REGIONS = ["All", "Hong Kong", "Greater China", "Singapore", "Philippines", "Australia", "New Zealand", "Asia Pacific", "Global"];

const ClientTile = ({ c }) => (
  <div
    data-testid={`clients-tile-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    className="bg-white border border-black/10 p-8 lg:p-10 flex flex-col items-center justify-center min-h-[200px] hover:border-[#E63946] transition-colors group"
  >
    <div className="flex-1 flex items-center justify-center w-full">
      <img
        src={c.url}
        alt={c.name}
        loading="lazy"
        onError={(e) => {
          const parent = e.target.parentElement;
          if (parent && !parent.dataset.fallback) {
            parent.dataset.fallback = "1";
            parent.innerHTML = `<span class='font-display font-bold text-zinc-700 text-lg tracking-tight text-center'>${c.name}</span>`;
          }
        }}
        className="max-h-16 max-w-[180px] object-contain"
      />
    </div>
    <div className="mt-6 pt-5 border-t border-black/10 w-full flex items-center justify-between">
      <span className="text-sm font-semibold text-black">{c.name}</span>
      <span className="text-[10px] tracking-[0.18em] uppercase text-zinc-500">{c.region}</span>
    </div>
  </div>
);

const Clients = () => {
  const [region, setRegion] = useState("All");

  const filtered = useMemo(
    () => (region === "All" ? CLIENTS : CLIENTS.filter((c) => c.region === region)),
    [region]
  );

  return (
    <div data-testid="page-clients">
      <PageHeader
        overline="Clients"
        title={<>Our <span className="text-[#E63946]">APAC partners.</span></>}
        kicker="Banks and law firms in Hong Kong. Hospitals across Singapore, Australia and New Zealand. Construction, retail, healthcare and government — twenty institutions across nine markets whose teams we've trained, coached or restructured."
      />

      <section className="border-b border-black/10 bg-[#F7F7F8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12 lg:py-14 flex flex-wrap gap-2">
          {REGIONS.map((r) => (
            <button
              key={r}
              data-testid={`region-filter-${r.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setRegion(r)}
              className={`px-4 py-2 text-xs tracking-[0.18em] uppercase font-semibold transition-colors border ${
                region === r
                  ? "bg-black text-white border-black"
                  : "bg-white text-zinc-700 border-black/10 hover:border-black"
              }`}
            >
              {r}
            </button>
          ))}
        </div>
      </section>

      <section className="border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {filtered.map((c) => (
              <ClientTile key={c.name} c={c} />
            ))}
          </div>
          {filtered.length === 0 && (
            <p className="text-center text-zinc-500 py-12">No clients in this region yet.</p>
          )}
        </div>
      </section>

      <section className="bg-[#09090B] text-white border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32">
          <p className="text-xs tracking-[0.22em] uppercase text-[#E63946] font-semibold mb-6">
            What clients say
          </p>
          <h2 className="font-display text-3xl lg:text-5xl font-black tracking-tighter leading-[1.05]">
            Outcomes our clients <span className="text-zinc-500">talk about.</span>
          </h2>
          <div className="mt-16 grid grid-cols-1 lg:grid-cols-3 gap-8">
            {TESTIMONIALS.map((t, i) => (
              <figure
                key={i}
                data-testid={`clients-testimonial-${i}`}
                className="border border-zinc-800 p-10 flex flex-col bg-[#0F0F12]"
              >
                <span className="font-display text-6xl text-[#E63946] leading-none">"</span>
                <blockquote className="mt-6 font-display text-lg lg:text-xl tracking-tight text-white leading-snug flex-1">
                  {t.quote}
                </blockquote>
                <figcaption className="mt-8 pt-6 border-t border-zinc-800">
                  <p className="text-sm font-semibold text-white">{t.name}</p>
                  <p className="text-xs text-zinc-500 mt-1">{t.company}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32 text-center">
          <h2 className="font-display text-3xl lg:text-5xl font-black tracking-tighter leading-[1.05] max-w-3xl mx-auto">
            Ready to add your organisation to this list?
          </h2>
          <Link
            to="/contact"
            data-testid="clients-bottom-cta"
            className="group mt-10 inline-flex items-center gap-2 bg-[#E63946] text-white px-7 py-4 text-sm font-semibold hover:bg-[#D32F2F] transition-colors"
          >
            Start a conversation <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Clients;
